import React, { Fragment } from "react";
import { Button, Form } from "semantic-ui-react";
import { DateInput } from "./DateInput";
import { getDefaultDateValue } from "./DateTimePicker.utils";
import { TimeInput } from "./TimeInput";
import _format from "date-fns/format";
import isPast from "date-fns/isPast";
import isSameDay from "date-fns/isSameDay";

import "./_DateTimePicker.less";
import { DATE_FORMAT, TIME_FORMAT } from "../../constants/formats";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { catchErrors, PickerUtils } from "./PickerUtils";

const getTextProps = (t: TFunction) => ({
    cannotBeInThePast: t("Ei voi olla menneisyydessä"),
});

export interface DateTimePickerProps {
    value?: string;
    onChange: (value?: string) => any;

    format?: "date" | "time" | "date-time";
    valueFormat?: "date" | "time" | "date-time" | "any";

    pastDateWarning?: boolean;

    defaultValue?: Date;
    defaultTime?: string;
    defaultValueBase?: Date;
    hourRoundingValue?: number;
    autoSelectTime?: boolean;

    minDate?: string;
    maxDate?: string;

    required?: boolean;
    disabled?: boolean;
    clearable?: boolean;
    error?: boolean;
}

const DateTimePicker: React.FC<DateTimePickerProps> = ({
    value,
    onChange,

    format = "date",
    valueFormat,
    hourRoundingValue = 1,
    autoSelectTime = true,
    defaultValueBase = new Date(),

    defaultValue,
    defaultTime,

    pastDateWarning,

    minDate,
    maxDate,

    disabled = false,
    required = false,
    clearable = true,
    error = false,
}) => {
    const { i18n } = useTranslation();

    const texts = getTextProps(useTranslation().t);

    valueFormat = !valueFormat ? format : valueFormat;
    const hideTimePicker = format.indexOf("time") === -1;
    const hideDatePicker = format === "time";

    const { date, time, dateInput, timeInput } = catchErrors(PickerUtils.separateDateAndTime, [value], {});

    const minDateAsDate = catchErrors(PickerUtils.valueToDate, [minDate], undefined);
    const maxDateAsDate = catchErrors(PickerUtils.valueToDate, [maxDate], undefined);

    const minDateIsOnSameDay = date && minDateAsDate && isSameDay(date, minDateAsDate);
    const maxDateIsOnSameDay = date && maxDateAsDate && isSameDay(date, maxDateAsDate);

    const dateIsInPast =
        pastDateWarning && date && time ? isPast(PickerUtils.overwriteTimePartOfDate(date, time)) : false;

    const update = (date: string | undefined, time: string | undefined) => {
        if (!disabled) {
            const format = date && time ? "date-time" : date ? "date" : time ? "time" : undefined;
            const processedValue = catchErrors(
                PickerUtils.processValue,
                [date, time, format || "date-time"],
                "invalid_value",
            );
            if (processedValue !== "invalid_value") {
                onChange(processedValue);
            }
        }
    };

    const getDefaultTime = (referenceDate: Date = new Date()): string | undefined => {
        if (valueFormat === "any" || !autoSelectTime) {
            return undefined;
        }
        if (defaultValue) {
            return _format(defaultValue, TIME_FORMAT);
        }

        const time = getDefaultDateValue(referenceDate, {
            minDate: referenceDate,
            defaultTime,
            hourRoundingValue,
        });

        let parsedValue = PickerUtils.overwriteTimePartOfDate(referenceDate, defaultValueBase);

        if (hourRoundingValue) {
            parsedValue = PickerUtils.roundToNearestHour(parsedValue, hourRoundingValue);
        }

        const timeValue = time && !isSameDay(new Date(time), new Date()) ? new Date(time) : parsedValue;
        return _format(timeValue, TIME_FORMAT);
    };

    const updateDateInput = (value?: string) => {
        const referenceDate = minDateAsDate; // minDateAsDate ? new Date(minDateAsDate.getTime()) : undefined;
        if (referenceDate) {
            referenceDate.setHours(referenceDate.getHours() + 1);
        }
        update(
            value,
            !timeInput && value && format !== "date"
                ? getDefaultTime(referenceDate || new Date())
                : timeInput,
        );
    };

    const updateTimeInput = (newTime?: string) => {
        update(dateInput, newTime);
    };

    const clear = () => {
        update(undefined, undefined);
    };

    return (
        <Fragment>
            <Form.Group className="DateTimePicker" inline>
                {!hideDatePicker ? (
                    <Form.Field disabled={disabled} error={dateIsInPast || error} required={required}>
                        <DateInput
                            value={dateInput}
                            onChange={updateDateInput}
                            minDate={minDateAsDate}
                            maxDate={maxDateAsDate}
                            errorMessage={
                                date && dateIsInPast && !isSameDay(date, new Date())
                                    ? texts.cannotBeInThePast
                                    : undefined
                            }
                            lang={i18n.language || "fi"}
                        />
                    </Form.Field>
                ) : null}

                {!hideTimePicker ? (
                    <Form.Field disabled={disabled} error={dateIsInPast || error} required={required}>
                        <TimeInput
                            key={String(minDateIsOnSameDay)}
                            value={timeInput}
                            onChange={updateTimeInput}
                            minDate={minDateIsOnSameDay || format === "time" ? minDateAsDate : undefined}
                            maxDate={maxDateIsOnSameDay || format === "time" ? maxDateAsDate : undefined}
                            errorMessage={
                                date && dateIsInPast && !isSameDay(date, new Date())
                                    ? texts.cannotBeInThePast
                                    : undefined
                            }
                        />
                    </Form.Field>
                ) : null}

                {clearable ? (
                    <Form.Field>
                        {date || time ? (
                            <Button
                                type="button"
                                disabled={disabled}
                                onClick={!disabled ? clear : undefined}
                                icon="times"
                                style={{ maxHeight: "45px" }}
                            />
                        ) : null}
                    </Form.Field>
                ) : null}
            </Form.Group>
        </Fragment>
    );
};

export default DateTimePicker;
